import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import {
  BodyText,
  NoBordersTable,
  StyledTd,
  StyledTh,
  TextButton,
} from 'src/components/atomsLegacy';
import { handleErrorToast } from 'src/components/molecules';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import { CaseDocumentationType } from 'src/graphql';
import {
  getScanStatusColor,
  getScanStatusErrorMessage,
  getWorstScanStatus,
  shouldBlockDownload,
} from 'src/utils';
import { DateFormats, formatDate } from 'src/utils/time';
import {
  Centered,
  CircleCheckbox,
  EmptyTableView,
  Img,
  StyledThead,
  StyledTr,
  TableContainer,
} from './CaseDocumentsTable.styles';

interface Props {
  documents: CaseDocumentationType[];
  selectedDocuments: CaseDocumentationType[];
  setSelectedDocuments: (selectedDocuments: CaseDocumentationType[]) => void;
  openDocument: (document: CaseDocumentationType) => void | Promise<void>;
}

export const CaseDocumentsTable: FC<Props> = ({
  documents,
  selectedDocuments,
  setSelectedDocuments,
  openDocument,
}) => {
  const reduxCase = useSelector(casePageVm);
  const theme = useTheme();

  const handleTableRowClick = (document: CaseDocumentationType) => {
    if (selectedDocuments.includes(document)) {
      setSelectedDocuments(selectedDocuments.filter((doc) => doc !== document));
    } else {
      setSelectedDocuments([...selectedDocuments, document]);
    }
  };

  const handleOpenDocument = (document: CaseDocumentationType) => {
    // Block workspace uploads if infected/pending, allow document editor to open
    if (document.s3path && document.Uploads?.length) {
      const scanStatus = getWorstScanStatus(document.Uploads);

      if (shouldBlockDownload(scanStatus)) {
        handleErrorToast({
          toastMessage: 'Download blocked',
          toastDescription: getScanStatusErrorMessage(scanStatus),
        });
        return;
      }
    }

    openDocument(document);
  };

  const userHasReadOnlyAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      !reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );
  return (
    <TableContainer>
      <NoBordersTable>
        <StyledThead>
          <tr>
            {!userHasReadOnlyAccess && (
              <StyledTh size="large">
                <CircleCheckbox
                  checked={
                    documents.length > 0 &&
                    selectedDocuments.length === documents.length
                  }
                  onChange={() => {
                    selectedDocuments.length === documents.length
                      ? setSelectedDocuments([])
                      : setSelectedDocuments(documents);
                  }}
                />
              </StyledTh>
            )}
            <StyledTh
              width="45%"
              size="large"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N1000
                  : theme.atlas.semantic.color.type.default
              }
            >
              File name
            </StyledTh>
            <StyledTh
              width="25%"
              size="large"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N1000
                  : theme.atlas.semantic.color.type.default
              }
            >
              Owner
            </StyledTh>
            <StyledTh
              width="25%"
              size="large"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N1000
                  : theme.atlas.semantic.color.type.default
              }
            >
              Last action
            </StyledTh>
            <StyledTh />
          </tr>
        </StyledThead>
        <tbody>
          {documents.length > 0 ? (
            documents.map((document) => {
              const scanStatus = document.Uploads?.length
                ? getWorstScanStatus(document.Uploads)
                : null;

              const fileNameColor = document.Uploads?.length
                ? getScanStatusColor(scanStatus, theme)
                : undefined;

              return (
                <StyledTr
                  data-testid={`document-${document.id}-row`}
                  onClick={() => handleOpenDocument(document)}
                  key={document.id}
                >
                  {!userHasReadOnlyAccess && (
                    <StyledTd>
                      <CircleCheckbox
                        data-testid={`document-${document.id}-checkbox`}
                        checked={selectedDocuments.includes(document)}
                        readOnly
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTableRowClick(document);
                        }}
                      />
                    </StyledTd>
                  )}
                  <StyledTd weight="light">
                    <Centered>
                      <span style={{ color: fileNameColor }}>
                        {document.title ?? 'Untitled document'}
                      </span>
                      {!document.s3path && document.Uploads?.length ? (
                        <Img
                          src="/assets/paperclip_attachment.svg"
                          alt="Attachment"
                        />
                      ) : null}
                    </Centered>
                  </StyledTd>
                  <StyledTd weight="light">{document.Author.name}</StyledTd>
                  <StyledTd weight="light">
                    {formatDate(
                      document.lastEdited,
                      DateFormats.DD_MM_YYYY_TIME_SHORT,
                    )}
                  </StyledTd>
                  {!userHasReadOnlyAccess && !document.s3path && (
                    <StyledTd weight="light">
                      <TextButton
                        label={'Edit'}
                        onClick={() => {
                          openDocument(document);
                        }}
                        grey
                        underlinedText
                        size="tiny"
                        data-testid={`edit-document-${document.id}-button`}
                      />
                    </StyledTd>
                  )}
                </StyledTr>
              );
            })
          ) : (
            <tr>
              <td colSpan={4}>
                <EmptyTableView data-testid="empty-case-documents-table">
                  <img
                    src="/assets/addFileGrey.svg"
                    alt="Add file icon"
                    width="92px"
                    height="101px"
                  />
                  <BodyText weight="regular">
                    You don&apos;t have any documents yet.
                  </BodyText>
                </EmptyTableView>
              </td>
            </tr>
          )}
        </tbody>
      </NoBordersTable>
    </TableContainer>
  );
};
